/**
 * Theme Provider Context
 * Provides theme state and switching functionality to the entire app
 */

import React, { createContext, useContext, useEffect, useState } from 'react';
import { THEME_CONFIG, tokens } from './tokens';
import createGlobalThemeStyles from './globalTheme';

// Create theme context
const ThemeContext = createContext();

/**
 * ThemeProvider Component
 * Wraps the app to provide theme context and global styles
 */
export const ThemeProvider = ({ children, defaultTheme = THEME_CONFIG.DARK }) => {
  const [theme, setTheme] = useState(() => {
    // Try to get theme from localStorage
    const savedTheme = localStorage.getItem('ifit-theme') || localStorage.getItem('app-theme');
    return savedTheme || defaultTheme;
  });

  // Update document attribute and localStorage when theme changes
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
    localStorage.setItem('ifit-theme', theme);
  }, [theme]);

  // Inject global styles on mount
  useEffect(() => {
    const styleId = 'global-theme-styles';
    if (!document.getElementById(styleId)) {
      const style = document.createElement('style');
      style.id = styleId;
      style.textContent = createGlobalThemeStyles();
      document.head.appendChild(style);
    }
  }, []);

  const toggleTheme = () => {
    setTheme((prev) => (prev === THEME_CONFIG.DARK ? THEME_CONFIG.LIGHT : THEME_CONFIG.DARK));
  };

  const switchTheme = (newTheme) => {
    if (Object.values(THEME_CONFIG).includes(newTheme)) {
      setTheme(newTheme);
    }
  };

  const value = {
    theme,
    toggleTheme,
    switchTheme,
    isDark: theme === THEME_CONFIG.DARK,
    isLight: theme === THEME_CONFIG.LIGHT,
    tokens,
  };

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
};

/**
 * useTheme Hook
 * Access theme context in any component
 */
export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within ThemeProvider');
  }
  return context;
};

/**
 * withTheme HOC
 * For class components that need theme access
 */
export const withTheme = (Component) => {
  return (props) => {
    const theme = useTheme();
    return <Component {...props} theme={theme} />;
  };
};

export default ThemeContext;
